import React, { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Timestamp } from '../Timestamp';
import { motion } from 'framer-motion';
import { Store } from '../../../store';
import messageReceivedAudio from 'url:./message-received.m4a';
import messageSentAudio from 'url:./message-sent.m4a';
import { useVendor } from '@outdoorly/react-chat-sdk';
import { OGImage } from '../OGImage';
import { useMemoizedFrame } from '../../../utils/hooks';

const MotionBox = motion(Box);

export enum MessageOptionActionType {
  AnimateIn = 'AnimateIn',
  Link = 'Link',
  Cancel = 'Cancel',
  CancelPII = 'CancelPII'
}

type ChatMessageProps = {
  body: JSX.Element;
  isLastMessageInGroup: boolean;
  isExpertOrSystem: boolean;
  dateUpdated: string;
  id: string;
  isIntroSequence?: boolean;
  isLastMessage: boolean;
  expertName?: string;
  disableAriaLive?: boolean;
};

type JSONValue = null | string | number | boolean | JSONObject;

type JSONObject = {
  [x: string]: JSONValue;
};

const messageReceived = new Audio(messageReceivedAudio);
const messageSent = new Audio(messageSentAudio);

export const ChatMessage: React.FC<ChatMessageProps> = React.memo((props: ChatMessageProps) => {
  const {
    body,
    isExpertOrSystem,
    isLastMessageInGroup,
    dateUpdated,
    id,
    isIntroSequence,
    isLastMessage,
    expertName,
    disableAriaLive
  } = props;

  const muted = Store.useState((s) => s.muted);
  const isActivatorVisible = Store.useState((s) => s.activatorVisible);
  const lastPlayedMessage = window.localStorage.getItem('lastPlayedMessage');

  useEffect(() => {
    const playAudio = async () => {
      (isExpertOrSystem ? messageReceived : messageSent).currentTime = 0;

      (isExpertOrSystem ? messageReceived : messageSent).play().catch(() => {});
    };

    if (isLastMessage && lastPlayedMessage !== id && !muted && !(isActivatorVisible && isIntroSequence)) {
      playAudio();
      window.localStorage.setItem('lastPlayedMessage', id);
    }
  }, []);
  const rowRef = useRef<HTMLTableRowElement | null>(null);
  const messageContainerRef = useRef<HTMLTableCellElement | null>(null);
  const { vendor } = useVendor();
  const { document } = useMemoizedFrame();
  return (
    <>
      <Flex
        justify={isExpertOrSystem ? '' : 'flex-end'}
        mt="4px"
        w="100%"
        role="row"
        ref={rowRef}
        pos="relative"
      >
        <MotionBox
          animate={{ height: 'auto', opacity: 1 }}
          initial={isLastMessage && lastPlayedMessage !== id ? { height: 0, opacity: 0 } : false}
          flexBasis={'80%'}
          ml={isExpertOrSystem ? '28px' : 0}
          key={id}
          role="none"
        >
          <Box
            role="gridcell"
            aria-label={
              !isExpertOrSystem ? `You, ` : expertName ? `${expertName}, ` : `${vendor?.name || 'System'}, `
            }
            p="12px"
            background={isExpertOrSystem ? 'scheme.backgroundSecondary' : 'scheme.whiteCompatible'}
            borderRadius="8px"
            maxWidth={'fit-content'}
            float={isExpertOrSystem ? 'none' : 'right'}
            tabIndex={isLastMessage ? 0 : -1}
            id={isLastMessage ? 'lastRemarkMessageId' : id}
            ref={messageContainerRef}
            aria-live={isLastMessage && isExpertOrSystem && !disableAriaLive ? 'polite' : 'off'}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && document?.activeElement === messageContainerRef?.current) {
                document.getElementById(`message-link-${id}-0`)?.focus();
              }
              //@todo add support for navigating multiple links
            }}
          >
            {body}
            <OGImage id={id} isExpert={isExpertOrSystem} />
          </Box>
        </MotionBox>
      </Flex>
      {isLastMessageInGroup && (
        <Text
          variant="label-small"
          textAlign={isExpertOrSystem ? 'left' : 'right'}
          mt="4px"
          color={'scheme.contentTertiary'}
          ml={isExpertOrSystem ? '30px' : 0}
          mr={isExpertOrSystem ? 0 : '4px'}
        >
          <Timestamp time={dateUpdated} />
        </Text>
      )}
    </>
  );
});
